import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FUTURE_SCHEDULED_PAYRUN } from '@xemplo/gp-constants';
import { ChangeTaskTypes, PayrunTypes } from '@xemplo/gp-types';
import { useGetBusinessUnitWorkersQuery } from '@xemplo/lookups-query';
import { useGetPayrunByIdQuery, useGetPayrunWorkersQuery } from '@xemplo/payrun-query';
import { useListBuilder } from '@xemplo/stateful-list-core';
import { Toggle, ToggleSize } from '@xemplo/switch';
import { V2 } from '@xemplo/text-dropdown';
import { V3 } from '@xemplo/text-dropdown-multi';

import { FormFields } from '../../../create-amendment.types';
import * as S from '../../modal.styles';

import { TerminatedWorkers } from './terminated-workers';

export const AppliesTo = ({ selectedWorkers }: { selectedWorkers?: string[] }) => {
  const { control, resetField, getValues, setValue } = useFormContext<FormFields>();

  const payrun = getValues('payrun');
  const businessUnit = useWatch({ control, name: 'businessUnit' });
  const changeTask = useWatch({ control, name: 'changeTask' });
  const terminatedWorkers = useWatch({ control, name: 'terminatedWorkers' });

  const [showTerminatedWorkers, setShowTerminatedWorkers] = useState(false);
  const isPayInstruction = changeTask === ChangeTaskTypes.PAY_INSTRUCTION;
  const isTermination = changeTask === ChangeTaskTypes.TERMINATION;
  const payrunId = !payrun || payrun === FUTURE_SCHEDULED_PAYRUN ? null : payrun;

  const { data: payrunData } = useGetPayrunByIdQuery({ id: payrunId });
  const { data: businessUnitWorkers, isLoading } = useGetBusinessUnitWorkersQuery({
    id: payrunData?.result?.businessUnitId ?? businessUnit ?? null,
    requestParams: { per_page: 0 },
  });

  const { data: payrunWorkers } = useGetPayrunWorkersQuery({
    id: payrunId ?? null,
    requestParams: { per_page: 500 },
  });

  const bussinessWorkersData = useMemo(() => {
    if (!businessUnitWorkers) return [];
    return businessUnitWorkers.result?.map(
      (item) => ({
        key: item.id.toString(),
        value: `${item.firstName} ${item.lastName}`,
      }),
      [],
    );
  }, [businessUnitWorkers]);

  const payrunWorkersData = useMemo(() => {
    if (!payrunWorkers?.result?.rows) return [];
    return payrunWorkers.result.rows.map((item) => ({
      key: item.id.toString(),
      value: `${item.firstName} ${item.lastName}`,
    }));
  }, [payrunWorkers]);

  const parsedData =
    payrunData?.result?.payrunTypeId === PayrunTypes.SCHEDULED
      ? payrunWorkersData
      : bussinessWorkersData;

  const workersList = useListBuilder(parsedData);

  /**
   * This is to cater for the Payrun mode where we have the selected workers passed in as props. We want to set the default value of the appliesTo field to the selected workers once the data is loaded.
   * We can't use the parent loader pattern here, so the effect sets the field value once the data is loaded.
   */
  useEffect(() => {
    if (!isLoading && selectedWorkers?.length && parsedData.length) {
      setValue('appliesTo', selectedWorkers, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [selectedWorkers, isLoading, setValue, parsedData]);

  const showTerminatedWorkersToggle = !isPayInstruction && !isTermination;

  const appliesToRules = {
    required:
      isPayInstruction || terminatedWorkers?.length ? false : 'This field is required',
  };

  return (
    <S.FormLabel>
      <S.AppliesToLabelWrapper>
        <div>
          Applies to {isPayInstruction && <S.OptionalLabel>(optional)</S.OptionalLabel>}
        </div>
        {showTerminatedWorkersToggle && (
          <S.TerminatedToggle>
            Include Terminated Workers
            <Toggle
              size={ToggleSize.Small}
              ariaLabel="toggle terminated workers"
              id="toggle-terminated-workers"
              value="toggle-terminated-workers"
              checked={showTerminatedWorkers}
              onChange={(e) => {
                setShowTerminatedWorkers(e.target.checked);
                if (!e.target.checked) {
                  resetField('terminatedWorkers');
                }
              }}
            />
          </S.TerminatedToggle>
        )}
      </S.AppliesToLabelWrapper>

      {isTermination ? (
        <V2.TextDropdownRhf
          name="appliesTo"
          items={workersList}
          placeholder="Select worker"
          aria-label="Applies to"
          data-testid="appliesTo"
          disabled={isLoading || !parsedData.length}
          rules={appliesToRules}
        />
      ) : (
        <V3.TextDropdownMultiRhf
          name="appliesTo"
          items={workersList}
          placeholder="Select worker(s)"
          aria-label="Applies to"
          data-testid="appliesTo"
          disabled={isLoading || !parsedData.length}
          rules={appliesToRules}
        />
      )}
      {showTerminatedWorkers && showTerminatedWorkersToggle && <TerminatedWorkers />}
    </S.FormLabel>
  );
};
